import React, { createContext, useContext, useState, useEffect } from 'react';
import { storeAPI } from '../lib/api';
import { storage, hasModule, MODULE_CODES } from '../lib/utils';

const StoreContext = createContext(null);

const LOG_PREFIX = '[StoreContext.selectStore]';

const normalizeStorePayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.store && typeof payload.store === 'object') return payload.store;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
};

const normalizeHeaders = (headers) => {
  if (!headers) return {};
  if (typeof headers.toJSON === 'function') return headers.toJSON();
  if (typeof headers.forEach === 'function') {
    const result = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
  return { ...headers };
};

const sanitizeHeaders = (headers) => {
  const normalized = normalizeHeaders(headers);
  if (normalized.Authorization) {
    normalized.Authorization = 'Bearer <redacted>';
  }
  if (normalized.authorization) {
    normalized.authorization = 'Bearer <redacted>';
  }
  return normalized;
};

const buildFullUrl = (config) => {
  if (!config) return null;
  const baseUrl = config.baseURL || '';
  const url = config.url || '';
  if (!baseUrl) return url;
  if (!url) return baseUrl;
  if (url.startsWith('http')) return url;
  return `${baseUrl}${url}`;
};

export function StoreProvider({ children }) {
  const [store, setStore] = useState(null);
  const [modules, setModules] = useState([]);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initStore = async () => {
      const savedSlug = localStorage.getItem('storeSlug');

      if (savedSlug) {
        try {
          const response = await storeAPI.getConfig(savedSlug, { skipStoreSlug: true });
          const { store: storeData, modules: storeModules, branding: storeBranding } = response.data || {};

          if (storeData) {
            setStore(storeData);
            setModules(storeModules || []);
            setBranding(storeBranding);
            applyBranding(storeBranding);
          }
        } catch (err) {
          console.error('Store config fetch failed:', err);
          localStorage.removeItem('storeSlug');
        }
      }
      setLoading(false);
    };

    initStore();
  }, []);

  const applyBranding = (brandingData) => {
    if (!brandingData) return;

    const root = document.documentElement;

    if (brandingData.primary_color) {
      // Convert hex to HSL for CSS variables
      const hsl = hexToHSL(brandingData.primary_color);
      if (hsl) {
        root.style.setProperty('--primary', hsl);
      }
    }
  };

  const hexToHSL = (hex) => {
    // Remove # if present
    hex = hex.replace('#', '');

    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
        default:
          h = 0;
      }
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };

  const selectStore = async (slug) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const requestHeaders = {};
      if (token) {
        requestHeaders.Authorization = 'Bearer <redacted>';
      }

      console.log(`${LOG_PREFIX} request`, {
        method: 'GET',
        url: '/api/public/store-config',
        headers: { ...requestHeaders, 'X-Store-Slug': slug },
        skipStoreSlug: true,
      });

      const response = await storeAPI.getConfig(slug, { skipStoreSlug: true });

      console.log(`${LOG_PREFIX} response`, {
        status: response.status,
        data: response.data,
        url: buildFullUrl(response.config),
        method: response.config?.method,
        headers: sanitizeHeaders(response.config?.headers),
      });

      const { store: storePayload, modules: storeModules, branding: storeBranding } = response.data || {};
      const storeData = normalizeStorePayload(storePayload);
      if (!storeData || !storeData.id || !storeData.slug) {
        throw new Error('Negozio non valido o non trovato');
      }

      localStorage.setItem('storeSlug', storeData.slug);
      setStore(storeData);
      setModules(storeModules || []);
      setBranding(storeBranding);
      applyBranding(storeBranding);
      return storeData;
    } catch (err) {
      console.log(`${LOG_PREFIX} error`, {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
        url: buildFullUrl(err?.config),
        method: err?.config?.method,
        headers: sanitizeHeaders(err?.config?.headers),
      });

      const message = err?.response?.data?.detail || err?.message || 'Errore nel caricamento del negozio';
      setError(message);
      localStorage.removeItem('storeSlug');
      setStore(null);
      setModules([]);
      setBranding(null);
      document.documentElement.style.removeProperty('--primary');

      if (window.location.pathname !== '/select-store') {
        window.location.assign('/select-store');
      }

      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const clearStore = () => {
    localStorage.removeItem('storeSlug');
    setStore(null);
    setModules([]);
    setBranding(null);
    // Reset branding to default
    document.documentElement.style.removeProperty('--primary');
  };

  const refreshStore = async () => {
    const savedSlug = localStorage.getItem('storeSlug');
    if (savedSlug) {
      try {
        const response = await storeAPI.getConfig(savedSlug, { skipStoreSlug: true });
        const { store: storeData, modules: storeModules, branding: storeBranding } = response.data || {};

        if (storeData) {
          setStore(storeData);
          setModules(storeModules || []);
          setBranding(storeBranding);
          applyBranding(storeBranding);
        }
      } catch (err) {
        console.error('Store refresh failed:', err);
      }
    }
  };

  const checkModule = (moduleCode) => {
    return hasModule(modules, moduleCode);
  };

  return (
    <StoreContext.Provider
      value={{
        store,
        modules,
        branding,
        loading,
        error,
        selectStore,
        clearStore,
        refreshStore,
        checkModule,
        hasShop: hasModule(modules, MODULE_CODES.SHOP),
        hasReportsAdv: hasModule(modules, MODULE_CODES.REPORTS_ADV),
        hasStaffPresence: hasModule(modules, MODULE_CODES.STAFF_PRESENCE),
        hasWhatsApp: hasModule(modules, MODULE_CODES.WHATSAPP),
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}
